import { Skeleton } from "@tql/ui-components-react/tql/indicators";

import type { Load } from "~/shared/schemas/loads-schema.ts";

import type { AdditionalDetails } from "../flyout-details.ts";
import styles from "./drawer-additional-details.module.css";
import { DrawerAdditionalDetailsLtl } from "./drawer-additional-details-ltl/drawer-additional-details-ltl.tsx";

type DrawerAdditionalDetailsProps = {
	readonly additionalDetails: AdditionalDetails | undefined;
	readonly isLoading: boolean;
	readonly load: Load | undefined | null;
};

export type AdditionalDetailsInfo = {
	emergencyContactName: string | null;
	emergencyContactNumber: string | null;
	customerReference: string | null;
	soNumber: string | null;
	poNumber: number | null;
	loadValue: number | null;
	hazmat: boolean | null;
	shipmentType: number | null;
	accessorial: string | null;
	specialInstructions: string | null;
	weight: number | null;
	customerPO: string | null;
};

function getDisplayValue(value: string | number | null | undefined): string {
	if (value === null || value === undefined || value === "") {
		return "-";
	}

	return String(value);
}

function formatWeight(value: number | null | undefined): string {
	if (value === null || value === undefined || value === 0) {
		return "-";
	}

	return `${value.toLocaleString("en-US")} lbs`;
}

function FieldItem({
	label,
	value,
	isLoading,
	fullWidth,
}: Readonly<{
	label: string;
	value: string;
	isLoading: boolean;
	fullWidth?: boolean;
}>) {
	const className = fullWidth
		? `${styles.infoItem} ${styles.fullWidth}`
		: styles.infoItem;

	return (
		<div className={className}>
			<p className={styles.label}>{label}</p>
			{isLoading ? (
				<span className={styles.loadingState}>
					<Skeleton animation={{ type: "wave" }} />
				</span>
			) : (
				<p className={styles.value}>{value}</p>
			)}
		</div>
	);
}

export function DrawerAdditionalDetails({
	additionalDetails,
	isLoading,
	load,
}: Readonly<DrawerAdditionalDetailsProps>) {
	const billRate =
		typeof additionalDetails?.billRate === "number" &&
		additionalDetails.billRate !== 0
			? new Intl.NumberFormat("en-US", {
					style: "currency",
					currency: "USD",
				}).format(additionalDetails.billRate)
			: null;

	if (load?.rateType === "LTL") {
		return (
			<DrawerAdditionalDetailsLtl
				additionalDetails={additionalDetails}
				billRate={billRate}
				isLoading={isLoading}
				load={load}
			/>
		);
	}

	const hasCustomerPO =
		(additionalDetails?.customerPO ?? load?.customerPO) != null;
	const hasTotalWeight = (additionalDetails?.weight ?? load?.weight) != null;
	const hasBillRate = billRate !== null;

	// For non-LTL loads
	return (
		<div className={styles.wrapper}>
			<section aria-label={"General information"} className={styles.section}>
				<div className={styles.infoGrid}>
					<FieldItem
						isLoading={isLoading ? !hasCustomerPO : false}
						label={"Customer PO"}
						value={getDisplayValue(
							additionalDetails?.customerPO ?? load?.customerPO,
						)}
					/>
					<FieldItem
						isLoading={isLoading ? !hasTotalWeight : false}
						label={"Total Weight"}
						value={formatWeight(additionalDetails?.weight ?? load?.weight)}
					/>
					{billRate === null ? null : (
						<FieldItem
							isLoading={isLoading ? !hasBillRate : false}
							label={"Bill Rate"}
							value={billRate}
						/>
					)}
				</div>
			</section>
		</div>
	);
}
